import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SubscriptionSection } from '../components/billing/SubscriptionSection';
import { Spinner } from '../design-system';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useOrganization } from '../contexts/OrganizationContext';

const SubscriptionRequiredPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAccess, loading, error } = useSubscriptionAccess();
  const { isAdmin, currentOrganization } = useOrganization();

  // Get the intended destination
  const from = (location.state as any)?.from?.pathname || '/repositories';
  const organizationName = currentOrganization?.name || 'this organization';
  const canManageBilling = isAdmin;

  // Redirect away as soon as org-level access becomes available.
  useEffect(() => {
    if (!loading && hasAccess) {
      navigate(from, { replace: true });
    }
  }, [hasAccess, loading, navigate, from]);

  useEffect(() => {
    if (!loading && error === 'auth') {
      navigate('/', { replace: true });
    }
  }, [error, loading, navigate]);

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-ui-background-primary'>
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-ui-background-primary text-ui-text-primary'>
      <div className='max-w-7xl mx-auto px-4 py-8'>
        {/* Header */}
        <div className='mb-8 text-center'>
          <h1 className='text-3xl font-bold'>Subscription Required for Organization</h1>
        </div>

        {/* Message */}
        <div className='max-w-3xl mx-auto mb-8'>
          {canManageBilling ? (
            <p className='text-xl text-ui-text-secondary text-center'>
              We hope you&apos;ve enjoyed your trial. To continue using Kolega.dev, you need to choose a plan.
            </p>
          ) : (
            <p className='text-xl text-ui-text-secondary text-center'>
              {organizationName}&apos;s free trial has ended. Please contact your organization admin or owner to
              continue.
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-8'>
            <p className='text-sm text-red-800 dark:text-red-200'>{error}</p>
          </div>
        )}

        {canManageBilling ? (
          <div className='bg-ui-background-secondary rounded-lg p-6'>
            <SubscriptionSection />
          </div>
        ) : (
          <div className='max-w-3xl mx-auto bg-ui-background-secondary rounded-lg p-6 border border-ui-border-primary'>
            <p className='text-sm text-ui-text-secondary'>
              Billing changes are restricted to organization admins and owners.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionRequiredPage; 
